// LayoutImpressao.tsx
//
// Aba "Layout de Impressão" de Lojas: o desenho do que sai na bobina — via de
// produção (cozinha) e via de conferência (cliente).
//
// A tela já existia em /settings/layout-impressao, fora do menu. Esta section é
// a mesma tela dentro da Configuração; a página virou casca fina, no molde de
// Site.tsx + SitePage.tsx. A rota continua valendo como deep link.
//
// DIVISÃO DE TRABALHO (a mesma de Site.tsx): esta section SABE SALVAR — escolhe
// o ambiente, fala com o service, carrega o token, decide o tom do aviso.
// `EditorLayoutComanda` só edita o desenho e devolve.
//
// De onde as lojas e os ambientes vêm: do `decryptedInfoToken`, não de HTTP —
// mesmo motivo de MeiosPagamento.tsx e Ambients.tsx (não existe rota de listagem
// que traga a travessia organization -> companies -> subsidiaries -> ambients).
//
// SELETORES DO TOPO, os três: "Loja / ambiente" é desta section (é ela que
// decide QUAL layout carregar); "Documento" e "Papel" ficam na barra do editor,
// logo abaixo, na mesma faixa do topo — duplicá-los aqui daria dois controles
// para a mesma coisa. O documento é do editor mas o ESTADO mora aqui: é ele que
// escolhe a rota do GET, então o editor avisa a troca por callback.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/LayoutImpressao";
import EditorLayoutComanda from "./EditorLayoutComanda";
import { layoutComandaService } from "../../../../services/layoutComanda.service";
import { useDecodedToken } from "../../../../services/jwt-decode.service";
import { useCombinedStore } from "../../../../store";
import {
  DocumentoComanda,
  LayoutComanda,
  layoutPadraoConferencia,
  layoutPadraoProducao,
} from "../../../../comum/layout-comanda";

interface Props {
  decryptedInfoToken: any;
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

interface AmbienteDaLista {
  ambientUuid: string;
  ambientName: string;
  storeName: string;
  companyName: string;
}

/**
 * O estado do caminho Central -> PDV, escrito na tela em vez de escondido.
 * Fica SEMPRE visível e de propósito não fala em "aplicado em N lojas": não
 * existe descida automática, e prometer isso seria a mesma mentira do
 * "Layout salvo com sucesso!" que o editor de salão dava.
 */
const AVISO_DESCIDA =
  "Este layout vale para o ambiente selecionado. Hoje o PDV lê o layout do " +
  "banco da loja (aod) e a Central grava no dela (acd) — a descida automática " +
  "ainda não existe; enquanto isso o PDV imprime pelo desenho em código.";

/** Os dois documentos que `src/comum/layout-comanda/tipos.ts` conhece. */
function padraoDoDocumento(documento: DocumentoComanda): LayoutComanda {
  return documento === "producao"
    ? layoutPadraoProducao()
    : layoutPadraoConferencia();
}

const LayoutImpressao: React.FC<Props> = ({ decryptedInfoToken }) => {
  const { authUserZustand } = useCombinedStore((state: any) => ({
    authUserZustand: state.authUserZustand,
  }));

  // `decryptedToken` NÃO vive na store: quem o deriva é este hook, a partir do
  // token de auth. Ler `state.decryptedToken` daria sempre undefined — e o
  // `updatedBy` iria vazio para uma coluna NOT NULL com FK.
  const decryptedToken: any = useDecodedToken(authUserZustand?.Token);

  // Travessia organization -> companies -> subsidiaries -> ambients, no molde do
  // useMemo de Ambients.tsx: `storeName` e `companyName` vêm junto porque o
  // seletor mostra a que loja o ambiente pertence.
  const ambientes: AmbienteDaLista[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).flatMap((filial: any) =>
        (filial?.ambients ?? []).map((ambiente: any) => ({
          ambientUuid: ambiente?.ambientUuid,
          ambientName: ambiente?.ambientName,
          storeName: filial?.subsidiaryName,
          companyName: empresa?.companyName,
        }))
      )
    );
  }, [decryptedInfoToken]);

  const [documento, setDocumento] = useState<DocumentoComanda>("producao");
  const [ambienteUuid, setAmbienteUuid] = useState<string>("");
  const [layout, setLayout] = useState<LayoutComanda | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  /** Sobe a cada carga: força o editor a reabrir com o layout novo. */
  const [versaoDoEditor, setVersaoDoEditor] = useState(0);

  // O primeiro ambiente é só o VALOR INICIAL do seletor, não a escolha às
  // escondidas que a página fazia antes: aqui o usuário troca quando quiser.
  const ambienteAtual = ambienteUuid || ambientes[0]?.ambientUuid || "";

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setAviso(null);
    layoutComandaService
      .buscar(documento, ambienteAtual || undefined)
      .then((resposta) => {
        if (!ativo) return;
        setLayout(resposta?.layout ?? padraoDoDocumento(documento));
        if (resposta && resposta.gravado === false) {
          setAviso({
            tom: "atencao",
            texto:
              "Este ambiente ainda não tem layout gravado. O que aparece é o " +
              "padrão do sistema — salvar cria o layout dele.",
          });
        }
        setVersaoDoEditor((v) => v + 1);
      })
      .catch(() => {
        if (!ativo) return;
        // Sem backend, abre no padrão do sistema em vez de tela branca. Mas o
        // aviso é de ATENÇÃO, não de sucesso: o que está na tela não é o que
        // está no servidor, e o dono precisa saber disso antes de salvar por
        // cima.
        setLayout(padraoDoDocumento(documento));
        setAviso({
          tom: "atencao",
          texto:
            "Não foi possível ler o layout gravado. Abrindo no padrão do " +
            "sistema — o que aparece aqui NÃO é o que está no servidor.",
        });
        setVersaoDoEditor((v) => v + 1);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [documento, ambienteAtual]);

  /** Devolve true quando gravou — o editor usa isso para limpar o "não salvo". */
  const salvar = useCallback(
    async (novoLayout: LayoutComanda): Promise<boolean> => {
      if (!ambienteAtual) {
        setAviso({
          tom: "erro",
          texto:
            "Nenhum ambiente para ligar o layout. `ActLayout` exige FK para " +
            'um ambiente — cadastre um em "Ambientes" antes de salvar.',
        });
        return false;
      }

      // O backend nunca populava `request.userUuid` e gravava undefined numa
      // coluna NOT NULL com FK; agora ele lê `updatedBy` do corpo. Sem quem
      // esteja salvando, recusa AQUI, antes de bater na rota.
      const updatedBy = decryptedToken?.userInfo?.userUuid;
      if (!updatedBy) {
        setAviso({
          tom: "erro",
          texto:
            "Não foi possível identificar quem está salvando (userUuid ausente " +
            "no token). Entre novamente e tente de novo — o servidor recusa a " +
            "gravação sem esse dado.",
        });
        return false;
      }

      setSalvando(true);
      try {
        const resposta = await layoutComandaService.salvar(
          documento,
          novoLayout,
          ambienteAtual,
          updatedBy
        );
        setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Layout salvo." });
        return true;
      } catch (erro: any) {
        setAviso({
          tom: "erro",
          texto:
            erro?.response?.data?.message ??
            "Não foi possível salvar o layout.",
        });
        return false;
      } finally {
        setSalvando(false);
      }
    },
    [ambienteAtual, decryptedToken, documento]
  );

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando lojas…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              print
            </AlfredoStyles.MaterialIcons>
            Layout de Impressão
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            O desenho da comanda que sai na bobina — o que você monta aqui é o
            que a impressora do PDV imprime
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {ambientes.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🖨️</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhum ambiente cadastrado
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            O layout é gravado por ambiente (é a impressora dele que recebe a
            comanda). Cadastre um em "Ambientes" para montar o layout.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        <>
          <AlfredoStyles.BarraSeletores>
            <AlfredoStyles.CampoSeletor>
              <AlfredoStyles.RotuloCampo>
                Loja / ambiente
              </AlfredoStyles.RotuloCampo>
              <AlfredoStyles.Seletor
                value={ambienteAtual}
                onChange={(e) => setAmbienteUuid(e.target.value)}
              >
                {ambientes.map((ambiente) => (
                  <option
                    key={ambiente.ambientUuid}
                    value={ambiente.ambientUuid}
                  >
                    {ambiente.storeName} — {ambiente.ambientName}
                  </option>
                ))}
              </AlfredoStyles.Seletor>
            </AlfredoStyles.CampoSeletor>
          </AlfredoStyles.BarraSeletores>

          <AlfredoStyles.Aviso $tom="atencao">{AVISO_DESCIDA}</AlfredoStyles.Aviso>

          {aviso && (
            <AlfredoStyles.Aviso $tom={aviso.tom}>
              {aviso.texto}
            </AlfredoStyles.Aviso>
          )}

          {carregando || !layout ? (
            <AlfredoStyles.LoadingContainer>
              <AlfredoStyles.LoadingSpinner />
              <div>Carregando layout…</div>
            </AlfredoStyles.LoadingContainer>
          ) : (
            <AlfredoStyles.PainelEditor>
              <EditorLayoutComanda
                key={`${documento}-${ambienteAtual}-${versaoDoEditor}`}
                layoutInicial={layout}
                encaixado
                salvando={salvando}
                onSalvar={salvar}
                onTrocarDocumento={setDocumento}
              />
            </AlfredoStyles.PainelEditor>
          )}
        </>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default LayoutImpressao;
